import React from 'react';
import SpecStateContext from './SpecStateContext';
import Button from '../component-renderer/Button';
import Input from '../component-renderer/Input';
import Table from '../component-renderer/Table';
import Title from '../component-renderer/Title';
import CheckBox from '../component-renderer/CheckBox';
import Card, { CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../component-renderer/Card';
import Tabs, { TabsList, TabsTrigger, TabsContent } from '../component-renderer/Tabs';
import Dropdown from '../component-renderer/Dropdown';
import FileUploadInput from '../component-renderer/FileUploadInput';
import SectionContainer from '../component-renderer/SectionContainer';
import Tag from '../component-renderer/Tag';
import Row from '../component-renderer/Row';
import Spacer from '../component-renderer/Spacer';
import Label from '../component-renderer/Label';
import Divider from '../component-renderer/Divider';
import Grid from '../component-renderer/Grid';
import DatePicker from '../component-renderer/DatePicker';
import TextArea from '../component-renderer/TextArea';
import Column from '../component-renderer/Column';
import Collapsible from '../component-renderer/Collapsible';
import List from '../component-renderer/List';
import Avatar from '../component-renderer/Avatar';
import Carousel from '../component-renderer/Carousel';
import Chart from '../component-renderer/Chart';
import LucideIcon from '../component-renderer/LucideIcon';
import uiComponentsSchema from './json-schemas/ui-components-schema.json';

// This file receives a normalized tree and converts it into real React elements.
// Think of it as a translator from data structure -> visible UI.

// Component types whose plain-string children are editable in preview mode.
const EDITABLE_TEXT_TYPES = new Set([
  'Title', 'CardTitle', 'CardDescription', 'Button', 'TabsTrigger',
]);

// Replace each plain string in a children array with a contentEditable span.
// Only called when editCtx.editMode is true and the component type is editable.
function wrapEditableText(children, nodeKey, editCtx) {
  if (!children) return children;
  return children.map((child, i) => {
    if (typeof child !== 'string' || !child.trim()) return child;
    const textKey = `${nodeKey}-t${i}`;
    const displayed = editCtx.overrides?.[textKey] ?? child;
    return (
      <span
        key={`ed-${textKey}`}
        contentEditable
        suppressContentEditableWarning
        onBlur={(e) => editCtx.onTextChange?.(textKey, e.currentTarget.textContent)}
        title="Click to edit"
        style={{
          outline: 'none',
          cursor: 'text',
          borderBottom: '1.5px dashed #cbd5e1',
          minWidth: '0.5em',
          display: 'inline',
          borderRadius: 2,
        }}
      >
        {displayed}
      </span>
    );
  });
}

// Small helper: if value is a number, convert it to "px" so CSS can use it.
function asPixel(v) {
  if (typeof v === 'number') return `${v}px`;
  if (typeof v === 'string' && /^\d+(\.\d+)?$/.test(v.trim())) return `${v.trim()}px`;
  return v;
}

// Minimal style builder for Container nodes.
// Supports simple flex/grid layout options from the schema.
function buildContainerStyle(props = {}) {
  const isGrid = props.layout === 'grid';
  return {
    display: isGrid ? 'grid' : 'flex',
    flexDirection: isGrid ? undefined : (props.flexDirection || props.direction || 'column'),
    gridTemplateColumns: isGrid && props.cols ? `repeat(${props.cols}, minmax(0, 1fr))` : undefined,
    gridColumn: props.gridColumn,
    gridRow: props.gridRow,
    flexWrap: props.flexWrap || (props.wrap ? 'wrap' : undefined),
    gap: asPixel(props.gap),
    padding: asPixel(props.padding),
    margin: asPixel(props.margin),
    minWidth: asPixel(props.minWidth),
    maxWidth: asPixel(props.maxWidth),
    minHeight: asPixel(props.minHeight),
    maxHeight: asPixel(props.maxHeight),
    height: asPixel(props.height),
    alignSelf: props.alignSelf,
    justifySelf: props.justifySelf,
    justifyContent: props.justifyContent || props.justify,
    alignItems: props.alignItems || props.align,
    background: props.background || props.backgroundColor,
    color: props.color,
    border: props.border,
    borderRadius: props.borderRadius ?? asPixel(props.radius),
    boxShadow: props.boxShadow ?? (props.shadow === 'sm' ? '0 1px 2px rgba(0,0,0,0.08)' : undefined),
    fontSize: props.fontSize,
    fontWeight: props.fontWeight,
    overflow: props.overflow,
    overflowX: props.overflowX,
    overflowY: props.overflowY,
    position: props.position,
    zIndex: props.zIndex,
    width: asPixel(props.width) ?? '100%',
  };
}

// Gets component names that are allowed to be rendered from the catalog JSON.
function getAllowedComponentTypes(registry = {}) {
  const componentNames = Array.isArray(uiComponentsSchema?.components)
    ? uiComponentsSchema.components.map((item) => item?.name).filter(Boolean)
    : [];

  return new Set(['Container', ...componentNames, ...Object.keys(registry || {})]);
}

// Small formatter bridge for table columns.
// In this test schema, "statusPill" means render a styled status badge.
function mapTableColumns(columns = []) {
  return columns.map((column) => {
    if (column?.format === 'statusPill') {
      return {
        ...column,
        render: (value) => (
          value === 'active' ? (
            <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs">Active</span>
          ) : (
            <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 text-xs">Inactive</span>
          )
        )
      };
    }

    return column;
  });
}

// Converts any prop value that is a spec/normalized node into a React element.
// This handles cases where the LLM places a spec node in a non-children prop
// (e.g. SectionContainer's `header`, `footer`, `actions` props).
function liftEmbeddedSpecNodes(props, registry, allowedTypes, keyPrefix, editCtx) {
  if (!props || typeof props !== 'object') return props;
  const result = { ...props };
  Object.entries(result).forEach(([propName, value]) => {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      if (typeof value.type === 'string' && allowedTypes.has(value.type)) {
        result[propName] = toReactNode(value, registry, allowedTypes, `${keyPrefix}-${propName}`, editCtx);
      } else if (value.children != null && typeof value.type === 'undefined') {
        const nodes = normalizeChildrenValue(
          value.children, registry, allowedTypes, `${keyPrefix}-${propName}`, editCtx
        );
        result[propName] = nodes.length === 1 ? nodes[0] : React.createElement(React.Fragment, {key: `${keyPrefix}-${propName}-frag`}, ...nodes);
      }
    } else if (Array.isArray(value)) {
      const hasNode = value.some(
        (v) => v && typeof v === 'object' && typeof v.type === 'string' && allowedTypes.has(v.type)
      );
      if (hasNode) {
        result[propName] = value.map((v, i) =>
          v && typeof v === 'object' && typeof v.type === 'string' && allowedTypes.has(v.type)
            ? toReactNode(v, registry, allowedTypes, `${keyPrefix}-${propName}-${i}`, editCtx)
            : v
        );
      }
    }
  });
  return result;
}

// Creates one React node from one normalized tree node.
// Special cases are kept simple and only handle what this test page needs.
function createNode(type, props, children, registry, allowedTypes, key, editCtx) {
  if (typeof type !== 'string' || !allowedTypes.has(type)) {
    throw new Error(`Unsupported component type in layout renderer: ${String(type)}`);
  }

  if (type === 'Container') {
    return (
      <div key={key} style={{ ...buildContainerStyle(props), ...(props.style || {}) }}>
        {children}
      </div>
    );
  }

  const Component = registry[type];
  if (!Component) {
    throw new Error(`Component type is allowed by schema but missing in registry: ${String(type)}`);
  }

  // Spread props WITHOUT key or children — children are always passed via JSX below.
  const { key: _omitKey, children: _rawChildren, ...rawProps } = props || {};
  const normalizedProps = liftEmbeddedSpecNodes(rawProps, registry, allowedTypes, key, editCtx);
  const rawPropsChildren = _rawChildren != null
    ? normalizeChildrenValue(_rawChildren, registry, allowedTypes, key, editCtx)
    : null;
  const resolvedChildren = children?.length ? children : rawPropsChildren;

  // Apply inline editing to string children for supported text component types.
  const finalChildren = (editCtx?.editMode && EDITABLE_TEXT_TYPES.has(type))
    ? wrapEditableText(resolvedChildren, key, editCtx)
    : resolvedChildren;

  if (type === 'Button') {
    const buttonChildren = finalChildren ?? normalizedProps.label;
    return <Component key={key} {...normalizedProps}>{buttonChildren}</Component>;
  }

  if (type === 'Input') {
    const nextProps = { ...normalizedProps };
    if (Object.prototype.hasOwnProperty.call(nextProps, 'value') && !nextProps.onChange) {
      nextProps.defaultValue = nextProps.value;
      delete nextProps.value;
    }
    if (nextProps.validationMessage?.visibleIf) {
      nextProps.error = nextProps.validationMessage.text || 'Invalid value';
    }
    delete nextProps.validationMessage;
    if (Object.prototype.hasOwnProperty.call(nextProps, 'value') && nextProps.value == null) {
      nextProps.value = '';
    }
    return <Component key={key} {...nextProps} />;
  }

  if (type === 'TextArea') {
    const nextProps = { ...normalizedProps };
    if (Object.prototype.hasOwnProperty.call(nextProps, 'value') && !nextProps.onChange) {
      nextProps.defaultValue = nextProps.value;
      delete nextProps.value;
    }
    if (Object.prototype.hasOwnProperty.call(nextProps, 'value') && nextProps.value == null) {
      nextProps.value = '';
    }
    return <Component key={key} {...nextProps} />;
  }

  if (type === 'Dropdown') {
    const nextProps = { ...normalizedProps };
    if (
      Object.prototype.hasOwnProperty.call(nextProps, 'value') &&
      !nextProps.onChange &&
      !nextProps.onValueChange
    ) {
      nextProps.defaultValue = nextProps.value;
      delete nextProps.value;
    }
    if (Object.prototype.hasOwnProperty.call(nextProps, 'value') && nextProps.value == null) {
      nextProps.value = '';
    }
    return <Component key={key} {...nextProps} />;
  }

  if (type === 'DatePicker') {
    const nextProps = { ...normalizedProps };
    if (
      Object.prototype.hasOwnProperty.call(nextProps, 'value') &&
      !nextProps.onChange &&
      !nextProps.onValueChange
    ) {
      nextProps.defaultValue = nextProps.value;
      delete nextProps.value;
    }
    if (Object.prototype.hasOwnProperty.call(nextProps, 'value') && nextProps.value == null) {
      nextProps.value = '';
    }
    return <Component key={key} {...nextProps} />;
  }

  if (type === 'CheckBox') {
    const nextProps = { ...normalizedProps };
    if (Object.prototype.hasOwnProperty.call(nextProps, 'checked') && !nextProps.onChange) {
      nextProps.defaultChecked = Boolean(nextProps.checked);
      delete nextProps.checked;
    }
    return <Component key={key} {...nextProps} />;
  }

  if (type === 'FileUploadInput') {
    const nextProps = { ...normalizedProps };
    if (
      Object.prototype.hasOwnProperty.call(nextProps, 'value') &&
      !nextProps.onChange &&
      !nextProps.onFilesChange
    ) {
      nextProps.defaultFiles = Array.isArray(nextProps.value) ? nextProps.value : [];
      delete nextProps.value;
    }
    return <Component key={key} {...nextProps} />;
  }

  if (type === 'Table') {
    const nextProps = {
      ...normalizedProps,
      columns: mapTableColumns(normalizedProps.columns),
      data: Array.isArray(normalizedProps.data) ? normalizedProps.data : []
    };
    return <Component key={key} {...nextProps} />;
  }

  if (type === 'Chart') {
    const nextProps = {
      ...normalizedProps,
      data: Array.isArray(normalizedProps.data) ? normalizedProps.data : []
    };
    return <Component key={key} {...nextProps} />;
  }

  if (type === 'Title') {
    return <Component key={key} {...normalizedProps}>{finalChildren}</Component>;
  }

  if (type === 'Tabs') {
    const nextProps = { ...normalizedProps };
    // The spec resolves $state.activeTab to a static string at render time with no
    // onValueChange handler, so the tab can never switch in controlled mode.
    // Convert to defaultValue so the Tabs component manages its own click state.
    if ('value' in nextProps && !nextProps.onValueChange) {
      nextProps.defaultValue = nextProps.value;
      delete nextProps.value;
    }
    return <Component key={key} {...nextProps}>{resolvedChildren}</Component>;
  }

  return <Component key={key} {...normalizedProps}>{finalChildren}</Component>;
}

// Normalise a raw children value from the spec into an array safe to iterate.
function normalizeChildrenValue(raw, registry, allowedTypes, keyPrefix, editCtx) {
  if (raw == null) return [];
  if (typeof raw === 'string' || typeof raw === 'number') return [raw];
  if (Array.isArray(raw))
    return raw
      .map((child, i) => toReactNode(child, registry, allowedTypes, `${keyPrefix}-${i}`, editCtx))
      .filter((c) => c !== null);
  // Single spec node object
  if (typeof raw === 'object' && raw.type)
    return [toReactNode(raw, registry, allowedTypes, `${keyPrefix}-0`, editCtx)].filter(Boolean);
  // Nested { children: [...] } wrapper the LLM sometimes emits
  if (typeof raw === 'object' && raw.children != null)
    return normalizeChildrenValue(raw.children, registry, allowedTypes, keyPrefix, editCtx);
  return [];
}

// Recursively converts the full tree into React nodes.
function toReactNode(node, registry, allowedTypes, key = 'root', editCtx = null) {
  if (node == null) return null;
  if (typeof node === 'string' || typeof node === 'number') return node;

  const childNodes = normalizeChildrenValue(node.children, registry, allowedTypes, key, editCtx);

  return createNode(node.type, node.props || {}, childNodes, registry, allowedTypes, key, editCtx);
}

// Tiny registry that maps schema component names to real React components.
export const defaultComponentRegistry = {
  Title,
  Button,
  Input,
  TextArea,
  Dropdown,
  DatePicker,
  Table,
  FileUploadInput,
  CheckBox,
  SectionContainer,
  Tag,
  Row,
  Spacer,
  Label,
  Divider,
  Grid,
  'Grid.Item': Grid.Item,
  Column,
  Collapsible,
  List,
  Avatar,
  Carousel,
  Chart,
  LucideIcon,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent
};

export function getMissingRegistryComponents(
  registry = defaultComponentRegistry,
  componentsSchema = uiComponentsSchema
) {
  const schemaNames = Array.isArray(componentsSchema?.components)
    ? componentsSchema.components.map((item) => item?.name).filter(Boolean)
    : [];

  return schemaNames.filter((name) => !Object.prototype.hasOwnProperty.call(registry, name));
}

// Public helper used by the route/page.
// Input: normalized tree.
// Output: renderable React node.
// options.editMode  — enable inline text editing
// options.overrides — { [textKey]: newText } map of user edits
// options.onTextChange — callback(textKey, newText) when a text node is edited
export function renderLayoutTree(tree, registry = defaultComponentRegistry, options = {}) {
  const allowedTypes = getAllowedComponentTypes(registry);
  const editCtx = options.editMode
    ? { editMode: true, overrides: options.overrides || {}, onTextChange: options.onTextChange || null }
    : null;
  const node = toReactNode(tree, registry, allowedTypes, 'root', editCtx);
  if (options.specState) {
    return (
      <SpecStateContext.Provider value={options.specState}>
        {node}
      </SpecStateContext.Provider>
    );
  }
  return node;
}

export default renderLayoutTree;